import { Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { extend } from 'leaflet';
import { BehaviorSubject, catchError, Observable, finalize, of, tap, switchMap, map, Subject, combineLatest, throwError } from 'rxjs';
import { take } from 'rxjs/operators';
import { GenerateXslx } from 'src/app/data/model/generateXslx';
import { UserTableVisualization } from 'src/app/data/model/user';


import {
  Pageable,
  PagedResponseList,
  ReadApiService,
} from '../../core/service/abstract/read-api.service';
import { CrudApiService } from '../../core/service/abstract/rest-api.service';
import { UserTableVisualizationApiService } from '../api/services/user-table-visualization-api.service';


export abstract class BaseListService<
  T extends { id: number | null },
  K extends CrudApiService<T>,
  P,
  Q extends ReadApiService<UserTableVisualization>
> {
  //public pagination:Pagination={page_num:150}
  private _sort$ = new BehaviorSubject<Sort>({active:"", direction:""})
  private _list$ = new BehaviorSubject<Array<T>>([]);
  private _columns$ = new BehaviorSubject<Array<UserTableVisualization>>([]);
  private _apiError$ = new BehaviorSubject<boolean>(false);
  private _count$ = new BehaviorSubject<number>(0);
  private _fulldata$ = new BehaviorSubject<Array<T>>([]);
  private _fulldatapage$ = new BehaviorSubject<Pageable>({ page: 0, pageSize: this._count$.value  }); //this._count$.value 
  private _page$ = new BehaviorSubject<Pageable>({ page: 0, pageSize: 10 }); 
  protected _filters$ = new BehaviorSubject<Filters>({});
  protected _client_side_filters$ = new BehaviorSubject<Filters>({});
  protected _report_filters$ = new BehaviorSubject<Filters>({});
  public _loading$:boolean=false;
  public _loadingSubject = new BehaviorSubject<boolean>(false);
  public _loadingMsg$ = new BehaviorSubject<string>("Si prega di attendere, aggiornamento in corso....");
  public observable_loading$ = this._loadingSubject.asObservable();
  public loadingMsg=this._loadingMsg$.asObservable();

  protected defaultDialogOptions = {
    width: '30%',
    disableClose: true,
  };
  public params:object |  null = null;
  constructor(protected dialog: MatDialog, protected apiService: K, protected columnService: Q) {

  }

  get sort$(): Observable<Sort> {
    return this._sort$.asObservable();
  }


  get columns$(): Observable<Array<UserTableVisualization>> {
    return this._columns$.asObservable();
  }

  get page$(): Observable<Pageable> {
    return this._page$.asObservable();
  }

  get fulldata$(): Observable<Array<T>> {
    return this._fulldata$.asObservable();
  }

  get fulldatapage$(): Observable<Pageable> {
    return this._fulldatapage$.asObservable();
  }

  get apiError$(): Observable<boolean> {
    return this._apiError$.asObservable();
  }

  get list$(): Observable<Array<T>> {
    return this._list$.asObservable();
  }

  get count$(): Observable<number> {
    return this._count$.asObservable();
  }

  get filters$(): Observable<Filters> {
    return this._filters$.asObservable();
  }

  get client_side_filters$(): Observable<Filters> {
    return this._client_side_filters$.asObservable();
  }

  get report_filters$(): Observable<Filters> {
    return this._report_filters$.asObservable();
  }

  getQueryParams(filters_params:any[]): { [key: string]: string | string[] } {
    const params: { [key: string]: string | string[] } = {};
  
    filters_params.forEach(filter => {
      if (!filter.field || filter.value === undefined || filter.value === null) return;
  
      if (Array.isArray(filter.value)) {
        params[filter.field] = filter.value.map((v:any) => String(v));
      } else {
        params[filter.field] = String(filter.value);
      }
    });
  
    return params;
  }


  setPagination(num:number){
    const page = this._page$.getValue()
    this._page$.next({...page, pageSize:num})
    
  }

  setPage(page: Pageable, sort?:Sort) {
    this._page$.next(page);
    if(sort === undefined || sort.direction===""){
      if (this.params != null){
        this.load(this.params)
      } else{
        this.load()
      }
    }
    else{
      this.sortGridData(sort);
    }
  }

  setFirstPage() {
    const page = this._page$.getValue();
    this._page$.next({ ...page, page: 0 });
  }

  setReportFilter(filter: object) {
    const current = this._report_filters$.getValue();
    this._report_filters$.next({ ...current, ...filter });
  }

  setFilter(filter: object) {
    const current = this._filters$.getValue();
    this._filters$.next({ ...current, ...filter });

    this.setFirstPage();
    if (this.params != null){
      this.load(this.params);
    }else{
      this.load();
    }
  }

  removeAllFilters() {
    this._filters$.next({});
  }

  setClientSideFilter(filter:object){

    const current = this._client_side_filters$.getValue();
    this._client_side_filters$.next({ ...current, ...filter });


    this.setFirstPage();
    this.filter(filter)

  }
  protected convertParams(req?: any): string | null {
    let params: string | undefined;;
    if (req) {
      for (const key in req) {
        if (typeof req[key] === 'undefined' || req[key] === null) continue;
        if (Array.isArray(req[key]) && req[key].length === 0) continue;

        params = req[key];
      }
    }

    return params!== undefined ? params : null;
  }

  nestedOdsFilter = (obj: any, filter: string | string[]): boolean => {
    if (obj.ods && obj.ods.ods_list) {
      const filters = Array.isArray(filter) ? filter : [filter];
      return obj.ods.ods_list.some((odsItem: any) => 
        filters.some(filter => 
          Object.values(odsItem).some(value => 
            String(value).includes(filter)
          )
        )
      );
    }
    return false;
  }
  

  filter(params: object) {
    this._apiError$.next(false);
    const filter = this.convertParams(params);
    if (filter === null) return; // null TODO:guard Check Before Production
    const keys=Object.keys(params)
    this.list$.pipe(
      take(1),
      map((list_data)=>list_data.filter(it => {
        if(keys.includes('citta_ods') || keys.includes('tipologia_rifiuto')){
          return this.nestedOdsFilter(it, filter);
        } else {
          return String(Object.values(it)[Object.keys(it).indexOf(keys[0])]).includes(filter)
        }
      }
      )),
    )
    .subscribe((list_data) => {
        this._count$.next(list_data.length);
        this._list$.next(list_data);
      }); 
  }

  removeClientSideFilter(key: string) {
    const current = this._client_side_filters$.getValue();
    if (!Object.hasOwnProperty.call(current, key)) return;

    delete current[key];

    this._client_side_filters$.next({ ...current });

    this.setFirstPage();
    if (this.params != null){
      this.load(this.params);
      if (current != null){
        this.filter(current)
      }
    }else{
      this.load();
    }
  }

  removeClientSideFilters(keys: string[]) {
    const current = this._client_side_filters$.getValue();

    for (const key of keys) {
      if (!Object.hasOwnProperty.call(current, key)) continue;
      delete current[key];
    }
    this._client_side_filters$.next({ ...current });

    this.setFirstPage();
    this.load();
  }

  removeFilter(key: string) {
    const current = this._filters$.getValue();
    if (!Object.hasOwnProperty.call(current, key)) return;

    delete current[key];

    this._filters$.next({ ...current });

    this.setFirstPage();
    
    if (this.params != null){
      this.load(this.params);
    }else{
      this.load();
    }
  }

  removeReportFilter(key: string) {
    const current = this._filters$.getValue();
    if (!Object.hasOwnProperty.call(current, key)) return;

    delete current[key];

    this._filters$.next({ ...current });
  }

  removeReportFilters(keys: string[]) {
    const current = this._filters$.getValue();
    for (const key of keys) {
      if (!Object.hasOwnProperty.call(current, key)) continue;
      delete current[key];
    }
    this._filters$.next({ ...current });

  }

  removeFilters(keys: string[]) {
    const current = this._filters$.getValue();
    for (const key of keys) {
      if (!Object.hasOwnProperty.call(current, key)) continue;
      delete current[key];
    }
    this._filters$.next({ ...current });

    this.setFirstPage();
    this.load();
  }

  
  loadColumns(module: string) {
    this._loadingSubject.next(true);
    this._apiError$.next(false);
  
    // Return the observable from columnService.getColumns
    return this.columnService
      .getColumns({ user: localStorage.getItem('user_id'), module_code: module })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          return of<PagedResponseList<UserTableVisualization>>({ count: 0, results: [] });
        }),
        finalize(() => {
          this._loadingSubject.next(false);
        })
      );
  }

  loadWithColumns(module: string, params: object | null = null) {
    this.loadColumns(module).pipe(
      switchMap((columns) => {
        this._columns$.next(columns.results);
        return this.loadObservable(params); // Only call load once loadColumns completes
      })
    ).subscribe((items) => {
      // Handle the result inside the subscription
      const current = this._client_side_filters$.getValue();
  
      this._list$.pipe(map(() => []));  // Reset list before updating
      this._count$.next(items.count);   // Update count
      this._list$.next(items.results);  // Update list

      if (Object.values(current).length > 0) {
        this.filter(current);
      }
    });
  }

  loadObservable(params: object | null = null) {
    this._loadingSubject.next(true);
    if (params !== null) {
      this.params = params;
    }
    this._apiError$.next(false);
  
    const page = this._page$.getValue();
    const filters = this._filters$.getValue();
    const fulldatapage = this._fulldatapage$.getValue();
    const current = this._client_side_filters$.getValue();
  
    return this.apiService
      .load(page, { ...params, ...filters })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          return of<PagedResponseList<T>>({ count: 0, results: [] });
        }),
        finalize(() => {
          this._loadingSubject.next(false); // ✅ Spinner hides after success or error
        })
      );
  }

  load(params: object | null = null) {
    this._loadingSubject.next(true);
    if (params!== null){
      this.params=params
    }
    this._apiError$.next(false);
    const page = this._page$.getValue();
    const filters = this._filters$.getValue();
    const fulldatapage = this._fulldatapage$.getValue();
    const current = this._client_side_filters$.getValue();
    
    this.apiService
      .load(page, { ...params, ...filters })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          return of<PagedResponseList<T>>({ count: 0, results: [] });
        }),
        finalize(() => {
          this._loadingSubject.next(false); // ✅ Spinner hides after success or error
        })
      )
      .subscribe((items) => {
        this._list$.pipe(
          map(() => [])
        );
        this._count$.next(items.count);
        this._list$.next(items.results);
        if(Object.values(current).length > 0){
          this.filter(current)
        }
      });
    //this._loadingSubject.next(false);
  }

  deleteItem(id: number, params: object | null = null) {
    this._loading$=true;
    this.apiService
      .deleteItem(id)
      .pipe(
        tap(() => this.load(params)),
        catchError((err) => {
          //TODO handle error
          console.log(err);
          return of(null);
        })
      )
      .subscribe();
    this._loading$=false;
  }

  getCSVdata(sort?:Sort, params: object | null = null){//sort:Sort
    if (params!== null){
      this.params=params
    }
    this._apiError$.next(false);
    const fulldatapage = this._fulldatapage$.getValue();
    const filters = this._filters$.getValue();
    //const sort = this._sort$.getValue();
    if (sort === undefined){
      this.apiService
      .load(fulldatapage, { ...params, ...filters })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          return of<PagedResponseList<T>>({ count: 0, results: [] });
        })
      )
      .subscribe((items) => {
        this._count$.next(items.count);
        this._fulldata$.next(items.results);
      });
    }
    else {
      this.apiService
      .getSorted(sort, fulldatapage, { ...params, ...filters })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          console.log(this._apiError$);
          return of<PagedResponseList<T>>({ count: 0, results: [] });
        })
      )
      .subscribe((items) => {
        this._count$.next(items.count);
        this._fulldata$.next(items.results);
      });
    }
  }

  generateXslx(body: GenerateXslx,  params: object | null = null, onError?: (err: any) => void) {
    if (params!== null){
      this.params=params
    }
    this._loadingSubject.next(true);
    const filters = this._filters$.getValue();
    this._loadingMsg$.next("Si prega di attendere, generazione report in corso....")
    // ✅ Make sure downloadExcel returns the Observable
    return this.apiService.downloadExcel(body, { ...params, ...filters })
      .pipe(
        finalize(() => {
          this._loadingSubject.next(false); // ✅ Spinner hides after success or error
          this._loadingMsg$.next("Si prega di attendere, aggiornamento in corso....")
        })
      )
      .subscribe({
        next: (res) => {
          const blob = new Blob([res.body!], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
      
          let filename = `${body.title}.xlsx`;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        },
      
        error: (err) => {
          if (onError) {
            onError(err);
          } else {
            console.error(err);
          }
        }
      });
  }
  
  sortGridData(sort:Sort, params: object | null = null){//sort:Sort
    this._loadingSubject.next(true);
    this._apiError$.next(false);
    const page = this._page$.getValue();
    const filters = this._filters$.getValue();

    this.apiService
      .getSorted(sort, page, { ...params, ...filters })
      .pipe(
        catchError(() => {
          this._apiError$.next(true);
          return of<PagedResponseList<T>>({ count: 0, results: [] });
        }),
        finalize(() => {
          this._loadingSubject.next(false); // ✅ Spinner hides after success or error
        })
      )
      .subscribe((items) => {
        this._count$.next(items.count);
        this._list$.next(items.results);
      });
  }
  protected abstract openDialog(dialogParams: P | null): Observable<boolean>;

  addItem(dialogParams: P | null = null): void {
    this.openDialog(dialogParams).subscribe((res) => {
      return this.subscribeToReload(res, dialogParams);
    });
  }

  /*changeState(entity:T): void {
    this._loadingSubject.next(true);
    this.apiService.update(entity).subscribe((res) =>{
      this.load();
    });
    this._loadingSubject.next(false);
  }*/

  changeState(entity: T, params?: object): Observable<any> {
    this._loadingSubject.next(true);
    return this.apiService.update(entity).pipe(
      tap(() => {
        if(params){
          this.load(params);
        } else {
          this.load();
        }
        this._loadingSubject.next(false);
      }),
      catchError((err) => {
        this._loadingSubject.next(false);
        return throwError(() => err); // forward error to caller
      })
    );
  }
  


  updatePointCoords(data:T){
    this.apiService.update(data).subscribe();
  }
    
  private hasId(obj: any): obj is { id: any } {
    return obj && typeof obj === 'object' && 'id' in obj;
  }
  
  updateItem(dialogParams: P | null = null): void {
    this.openDialog(dialogParams).subscribe((res) =>{
        if (this.hasId(dialogParams)) {
          delete dialogParams.id; 
        }
        return this.subscribeToReload(res, dialogParams)
      }
    );
  }

  updateUserItem(dialogParams: P | null = null): void {
    this.openDialog(dialogParams).subscribe((res) =>{
        if (this.hasId(dialogParams)) {
          delete dialogParams.id; 
        }
        return this.subscribeToReload(res, null)
      }
    );
  }




  private subscribeToReload(res: boolean, dialogParams: any) {
    if (res) this.load(dialogParams);
    if (res == null && dialogParams == null) this.load() 
    else null
  }
}

type Filters = { [key: string]: unknown };
type Pagination= {page_num:number}
